import React, { useState, useEffect, useMemo } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    Tag,
    Space,
    Popconfirm,
    Card,
    Typography,
    message,
    Empty,
    Statistic,
    Row,
    Col,
    Divider,
    Tooltip,
    Segmented,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    MinusCircleOutlined,
    SearchOutlined,
    FontSizeOutlined,
    NumberOutlined,
    CalendarOutlined,
    DownSquareOutlined,
    CheckSquareOutlined,
    UnorderedListOutlined,
    AppstoreOutlined,
    TableOutlined,
} from "@ant-design/icons";
import Hexapi from "../../../utility/HexAPI/Hexapi";

const { Title, Text } = Typography;

const BRAND = "#f59e0b";
const BRAND_SOFT = "#fffbeb";

const FIELD_TYPES = [
    { value: "text", label: "Text Input", icon: <FontSizeOutlined /> },
    { value: "number", label: "Number Input", icon: <NumberOutlined /> },
    { value: "date", label: "Date Picker", icon: <CalendarOutlined /> },
    { value: "select", label: "Dropdown Select", icon: <DownSquareOutlined /> },
    { value: "checkbox", label: "Checkbox", icon: <CheckSquareOutlined /> },
    {
        value: "multiselect",
        label: "Multi-Select Checkboxes",
        icon: <UnorderedListOutlined />,
    },
];

const FIELD_TYPE_MAP = FIELD_TYPES.reduce((acc, t) => {
    acc[t.value] = t;
    return acc;
}, {});

const DEFAULT_SECTIONS = [
    "Customer Details",
    "Customer Enquiry",
    "Additional Information",
];

export default function CustomFieldMaster() {
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [sectionFilter, setSectionFilter] = useState("All");

    const [form] = Form.useForm();
    const selectedFieldType = Form.useWatch("FieldType", form);

    // Fetch Fields List
    const fetchCustomFields = async () => {
        setLoading(true);
        try {
            const resp = await Hexapi({
                query: `[dbo].[CRM_Proc_CustomFields_GetList]`,
                queryArr: [],
            });
            const resultData = resp?.[""] || resp?.customFields || [];
            setFields(resultData);
        } catch (err) {
            console.error("Error loading fields:", err);
            message.error("Failed to load custom fields.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomFields();
    }, []);

    // Extract unique section names dynamically for the dropdown
    // Default sections hamesha available rahenge, DB se aane wale extra sections bhi merge honge
    const existingSections = useMemo(() => {
        const sectionsSet = new Set(DEFAULT_SECTIONS);

        fields.forEach((item) => {
            const sec = item.sectionname || item.sectionName || item.section;
            if (sec) sectionsSet.add(sec);
        });

        return Array.from(sectionsSet).map((sec) => ({ value: sec, label: sec }));
    }, [fields]);

    const sectionFilterOptions = useMemo(
        () => ["All", ...existingSections.map((s) => s.value)],
        [existingSections]
    );

    // Header stats
    const stats = useMemo(() => {
        const total = fields.length;
        const active = fields.filter((f) => !!(f.isactive ?? f.isActive)).length;
        const required = fields.filter(
            (f) => !!(f.isrequired ?? f.isRequired)
        ).length;
        const sections = new Set(
            fields.map((f) => f.sectionname || f.sectionName).filter(Boolean)
        ).size;
        return { total, active, required, sections };
    }, [fields]);

    // Filtered rows shown in the table
    const filteredFields = useMemo(() => {
        return fields.filter((item) => {
            const label = (item.label || "").toLowerCase();
            const key = (item.fieldkey || item.fieldKey || "").toLowerCase();
            const section = item.sectionname || item.sectionName || "";

            const matchesSearch =
                !searchText ||
                label.includes(searchText.toLowerCase()) ||
                key.includes(searchText.toLowerCase());
            const matchesSection =
                sectionFilter === "All" || section === sectionFilter;

            return matchesSearch && matchesSection;
        });
    }, [fields, searchText, sectionFilter]);

    // Handle Modal Open for Add/Edit
    const handleOpenModal = (record = null) => {
        setEditingRecord(record);
        if (record) {
            let parsedOptions = [""];
            const rawOptions = record.optionsjson || record.optionsJSON;
            if (rawOptions) {
                try {
                    parsedOptions =
                        typeof rawOptions === "string"
                            ? JSON.parse(rawOptions)
                            : rawOptions;
                } catch {
                    parsedOptions = [""];
                }
            }

            form.setFieldsValue({
                FieldID: record.fieldid ?? record.fieldID ?? 0,
                FieldKey: record.fieldkey ?? record.fieldKey ?? "",
                Label: record.label ?? "",
                FieldType: record.fieldtype ?? record.fieldType ?? "text",
                SectionName:
                    record.sectionname ?? record.sectionName ?? "Additional Information",
                IsRequired: !!(record.isrequired ?? record.isRequired),
                DefaultValue: record.defaultvalue ?? record.defaultValue ?? "",
                optionsList:
                    Array.isArray(parsedOptions) && parsedOptions.length > 0
                        ? parsedOptions
                        : [""],
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                FieldID: 0,
                FieldType: "text",
                SectionName: "Additional Information",
                IsRequired: false,
                optionsList: [""],
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        form.resetFields();
        setEditingRecord(null);
        setIsModalOpen(false);
    };

    // Auto-generate FieldKey from Label if creating new
    const handleLabelChange = (e) => {
        const val = e.target.value;
        if (!editingRecord) {
            const autoKey = val.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
            form.setFieldValue("FieldKey", autoKey);
        }
    };

    // Handle Form Submit
    const handleSubmit = async (values) => {
        const isChoiceType =
            values.FieldType === "select" || values.FieldType === "multiselect";
        const cleanOptions = isChoiceType
            ? (values.optionsList || []).filter((opt) => opt && opt.trim() !== "")
            : [];

        if (isChoiceType && cleanOptions.length === 0) {
            message.warning("Please add at least one valid option.");
            return;
        }

        const payloadOptions = isChoiceType ? JSON.stringify(cleanOptions) : "";

        try {
            const resp = await Hexapi({
                query: `[dbo].[CRM_Proc_CustomFields_Save] 
                    @FieldID='{0}',
                    @FieldKey='{1}',
                    @Label='{2}',
                    @FieldType='{3}',
                    @SectionName='{4}',
                    @IsRequired='{5}',
                 
                    @OptionsJSON='{6}'`,
                queryArr: [
                    values.FieldID || 0,
                    values.FieldKey,
                    values.Label,
                    values.FieldType,
                    values.SectionName || "Additional Information",
                    values.IsRequired ? 1 : 0,

                    payloadOptions,
                ],
            });

            const resArr = resp?.[""] || resp?.result || [];
            if (resArr[0]?.status === 1 || resArr[0]?.STATUS === 1) {
                message.success(
                    editingRecord
                        ? "Field updated successfully!"
                        : "Field created successfully!"
                );
                handleCloseModal();
                fetchCustomFields();
            } else {
                message.error(
                    resArr[0]?.msg || resArr[0]?.MSG || "Failed to save custom field."
                );
            }
        } catch (err) {
            console.error("Save error:", err);
            message.error("An error occurred while saving.");
        }
    };

    // Toggle Active / Inactive Status
    const handleToggleStatus = async (fieldID, currentStatus) => {
        try {
            await Hexapi({
                query: `[dbo].[CRM_Proc_CustomFields_ToggleStatus] @FieldID='{0}', @IsActive='{1}'`,
                queryArr: [fieldID, currentStatus ? 0 : 1],
            });
            message.success("Status updated successfully.");
            fetchCustomFields();
        } catch (err) {
            console.error("Toggle error:", err);
            message.error("Failed to update status.");
        }
    };

    // Delete Custom Field
    const handleDeleteField = async (fieldID) => {
        try {
            const resp = await Hexapi({
                query: `[dbo].[CRM_Proc_CustomFields_Delete] @FieldID='{0}'`,
                queryArr: [fieldID],
            });
            const resArr = resp?.[""] || resp?.result || [];
            if (resArr[0]?.status === 1 || resArr[0]?.STATUS === 1) {
                message.success("Custom field deleted successfully.");
                fetchCustomFields();
            } else {
                message.error(resArr[0]?.msg || "Failed to delete field.");
            }
        } catch (err) {
            console.error("Delete error:", err);
            message.error("Error occurred while deleting field.");
        }
    };

    // Table Columns
    const columns = [
        {
            title: "Field",
            dataIndex: "label",
            key: "label",
            render: (text, record) => {
                const typeVal = record.fieldtype || record.fieldType;
                const meta = FIELD_TYPE_MAP[typeVal];
                const fieldKey = record.fieldkey || record.fieldKey;
                return (
                    <Space size={10} align="center">
                        <div
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 8,
                                background: BRAND_SOFT,
                                color: BRAND,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 15,
                                flexShrink: 0,
                            }}
                        >
                            {meta?.icon || <FontSizeOutlined />}
                        </div>
                        <div style={{ lineHeight: 1.3 }}>
                            <div style={{ fontWeight: 600 }}>{text}</div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <code>{fieldKey}</code>
                            </Text>
                        </div>
                    </Space>
                );
            },
        },
        {
            title: "Section",
            dataIndex: "sectionname",
            key: "sectionname",
            render: (text, record) => {
                const val = text || record.sectionName || "Additional Information";
                return <Tag color="blue">{val}</Tag>;
            },
            filters: sectionFilterOptions
                .filter((s) => s !== "All")
                .map((s) => ({ text: s, value: s })),
            onFilter: (value, record) =>
                (record.sectionname || record.sectionName) === value,
        },
        {
            title: "Type",
            dataIndex: "fieldtype",
            key: "fieldtype",
            render: (type, record) => {
                const val = type || record.fieldType;
                const meta = FIELD_TYPE_MAP[val];
                return <Tag color="gold">{meta?.label || val}</Tag>;
            },
        },
        {
            title: "Required",
            dataIndex: "isrequired",
            key: "isrequired",
            align: "center",
            render: (req, record) => {
                const isReq = req ?? record.isRequired;
                return isReq ? (
                    <Tag color="red">Required</Tag>
                ) : (
                    <Tag>Optional</Tag>
                );
            },
        },
        {
            title: "Status",
            dataIndex: "isactive",
            key: "isactive",
            render: (isActive, record) => {
                const activeVal = isActive ?? record.isActive;
                const idVal = record.fieldid ?? record.fieldID;
                return (
                    <Switch
                        checked={!!activeVal}
                        onChange={() => handleToggleStatus(idVal, activeVal)}
                        checkedChildren="Active"
                        unCheckedChildren="Inactive"
                    />
                );
            },
        },
        {
            title: "",
            key: "action",
            width: 96,
            render: (_, record) => {
                const idVal = record.fieldid ?? record.fieldID;
                return (
                    <Space size={4}>
                        <Tooltip title="Edit field">
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => handleOpenModal(record)}
                            />
                        </Tooltip>
                        <Popconfirm
                            title="Delete this field?"
                            description="This can't be undone."
                            onConfirm={() => handleDeleteField(idVal)}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                        >
                            <Tooltip title="Delete field">
                                <Button type="text" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    return (
        <div style={{ padding: 24, background: "#f5f6f8", minHeight: "100%" }}>
            {/* Header */}
            <Card
                style={{
                    marginBottom: 16,
                    borderRadius: 12,
                    border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: 20 }}
            >
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col>
                        <Title level={4} style={{ margin: 0 }}>
                            Custom Fields
                        </Title>
                        <Text type="secondary">
                            These fields are displayed on the Add Lead form
                        </Text>
                    </Col>
                    {stats.total > 0 && (
                        <Col>
                            <Button
                                type="primary"
                                size="large"
                                icon={<PlusOutlined />}
                                onClick={() => handleOpenModal()}
                                style={{
                                    backgroundColor: BRAND,
                                    borderColor: BRAND,
                                }}
                            >
                                Field
                            </Button>
                        </Col>
                    )}
                </Row>

                <Divider style={{ margin: "16px 0" }} />

                <Row gutter={24}>
                    <Col span={6}>
                        <Statistic title="Total Fields" value={stats.total} />
                    </Col>
                    <Col span={6}>
                        <Statistic
                            title="Active"
                            value={stats.active}
                            valueStyle={{ color: "#16a34a" }}
                        />
                    </Col>
                    <Col span={6}>
                        <Statistic title="Required" value={stats.required} />
                    </Col>
                    <Col span={6}>
                        <Statistic title="Sections" value={stats.sections} />
                    </Col>
                </Row>
            </Card>

            {/* Toolbar */}
            <Card
                style={{ marginBottom: 16, borderRadius: 12, border: "1px solid #f0f0f0" }}
                bodyStyle={{ padding: 12 }}
            >
                <Row justify="space-between" align="middle" gutter={[12, 12]}>
                    <Col flex="320px">
                        <Input
                            allowClear
                            placeholder="Search by label or field key"
                            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </Col>
                    <Col flex="auto">
                        <Segmented
                            value={sectionFilter}
                            onChange={setSectionFilter}
                            options={sectionFilterOptions}
                        />
                    </Col>
                </Row>
            </Card>

            {/* Table */}
            <Card
                bodyStyle={{ padding: 0 }}
                style={{ borderRadius: 12, border: "1px solid #f0f0f0" }}
            >
                <Table
                    columns={columns}
                    dataSource={filteredFields}
                    rowKey={(rec) => rec.fieldid ?? rec.fieldID}
                    loading={loading}
                    pagination={{ pageSize: 8, hideOnSinglePage: true }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    fields.length === 0
                                        ? "No custom fields yet — add one to get started"
                                        : "No fields match your search"
                                }
                                style={{ padding: "32px 0" }}
                            >
                                {fields.length === 0 && (
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => handleOpenModal()}
                                        style={{
                                            backgroundColor: BRAND,
                                            borderColor: BRAND,
                                        }}
                                    >
                                        Field
                                    </Button>
                                )}
                            </Empty>
                        ),
                    }}
                />
            </Card>

            {/* Create / Edit Modal */}
            <Modal
                title={editingRecord ? "Edit Custom Field" : "Create Custom Field"}
                open={isModalOpen}
                onCancel={handleCloseModal}
                onOk={() => form.submit()}
                okText="Save Field"
                width={560}
                okButtonProps={{
                    style: {
                        backgroundColor: BRAND,
                        borderColor: BRAND,
                    },
                }}
                bodyStyle={{
                    maxHeight: "65vh",
                    overflowY: "auto",
                    paddingRight: 8,
                }}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        FieldID: 0,
                        FieldType: "text",
                        SectionName: "Additional Information",
                        IsRequired: false,
                        optionsList: [""],
                    }}
                >
                    <Form.Item name="FieldID" hidden>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="SectionName"
                        label="Section"
                        tooltip="Where this field appears on the enquiry form"
                        rules={[
                            {
                                required: true,
                                message: "Please select or type a section name!",
                            },
                        ]}
                    >
                        <Select
                            mode="combobox"
                            placeholder="Select an existing section or type a new one"
                            options={existingSections}
                            allowClear
                        />
                    </Form.Item>

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item
                                name="Label"
                                label="Field Label"
                                rules={[
                                    { required: true, message: "Please input the label!" },
                                ]}
                            >
                                <Input
                                    placeholder="e.g. GST Number"
                                    onChange={handleLabelChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="FieldKey"
                                label="Field Key"
                                tooltip="Unique identifier used internally — auto-filled from the label"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please input unique field key!",
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="e.g. gstNumber"
                                    disabled={!!editingRecord}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="FieldType" label="Field Input Type" rules={[{ required: true }]}>
                        <Select
                            options={FIELD_TYPES.map((t) => ({
                                value: t.value,
                                label: (
                                    <Space>
                                        {t.icon}
                                        {t.label}
                                    </Space>
                                ),
                            }))}
                        />
                    </Form.Item>

                    {(selectedFieldType === "select" ||
                        selectedFieldType === "multiselect") && (
                        <Card
                            size="small"
                            title="Dropdown Options"
                            style={{
                                marginBottom: 16,
                                backgroundColor: "#fafafa",
                                borderRadius: 8,
                            }}
                        >
                            <Form.List name="optionsList">
                                {(subFields, { add, remove }) => (
                                    <>
                                        <div
                                            style={{
                                                maxHeight: 220,
                                                overflowY: "auto",
                                                paddingRight: 8,
                                                marginBottom: 8,
                                            }}
                                        >
                                            {subFields.map(({ key, name, ...restField }) => (
                                                <Space
                                                    key={key}
                                                    style={{ display: "flex", marginBottom: 8 }}
                                                    align="baseline"
                                                >
                                                    <Text
                                                        type="secondary"
                                                        style={{ width: 18, textAlign: "right" }}
                                                    >
                                                        {name + 1}
                                                    </Text>
                                                    <Form.Item
                                                        {...restField}
                                                        name={name}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: "Missing option value",
                                                            },
                                                        ]}
                                                        style={{ flex: 1, marginBottom: 0 }}
                                                    >
                                                        <Input placeholder={`Option ${name + 1}`} />
                                                    </Form.Item>
                                                    {subFields.length > 1 && (
                                                        <MinusCircleOutlined
                                                            onClick={() => remove(name)}
                                                            style={{ color: "#ff4d4f" }}
                                                        />
                                                    )}
                                                </Space>
                                            ))}
                                        </div>
                                        <Button
                                            type="dashed"
                                            onClick={() => add()}
                                            block
                                            icon={<PlusOutlined />}
                                        >
                                            Add Option
                                        </Button>
                                    </>
                                )}
                            </Form.List>
                        </Card>
                    )}

                    <Form.Item
                        label="Required Field?"
                        name="IsRequired"
                        valuePropName="checked"
                    >
                        <Switch checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}